import { apiClient } from "./client";

export interface CurvePoint {
  power_kw: number;
  cost_uah_per_mwh: number;
}

/** Точка паспортної кривої: повна собівартість і її газова частина. */
export interface CostCurvePoint extends CurvePoint {
  gas_uah_per_mwh: number;
}

/** Групи постійних статей — ті самі, що в фільтрі на «Фінансах». */
export const FIXED_COST_GROUPS = ["rent", "service", "consumables", "other"] as const;
export type FixedCostGroup = (typeof FIXED_COST_GROUPS)[number];

export interface CostCurveResponse {
  date: string;
  power_min_kw: number;
  power_max_kw: number;
  power_step_kw: number;
  points: CostCurvePoint[];
  presets: Record<string, CostCurvePoint>;
  /** Постійні статті, грн/год. Собівартість без частини статей =
   *  gas_uah_per_mwh + Σ увімкнених / (P / 1000). */
  fixed_uah_per_hour: Partial<Record<FixedCostGroup, number>>;
}

export type CostGroupToggle = "gas" | FixedCostGroup;

/** Собівартість у трьох режимах (min / середина / max) з урахуванням лише
 *  УВІМКНЕНИХ статей (FR-UI-COST-05, сторінка «Газ»). */
export function costByGroups(
  data: Pick<CostCurveResponse, "presets" | "fixed_uah_per_hour">,
  enabled: Record<CostGroupToggle, boolean>,
): { power_kw: number; cost_uah_per_mwh: number }[] {
  const fixed = FIXED_COST_GROUPS.reduce(
    (acc, g) => acc + (enabled[g] ? data.fixed_uah_per_hour[g] ?? 0 : 0),
    0,
  );
  return Object.values(data.presets)
    .sort((a, b) => a.power_kw - b.power_kw)
    .map((p) => ({
      power_kw: p.power_kw,
      cost_uah_per_mwh:
        (enabled.gas ? p.gas_uah_per_mwh : 0) +
        (p.power_kw > 0 ? fixed / (p.power_kw / 1000) : 0),
    }));
}

export const costCurveApi = {
  get: (date?: string) =>
    apiClient
      .get<CostCurveResponse>("/cost-curve", { params: date ? { date } : undefined })
      .then((r) => r.data),
};
